import { defineStore } from 'pinia';
import { useConnectionStore } from '@/stores/connection';
import {
  type CompleteOrderInput,
  type CreatePaymentInformationInput,
  type Mutation,
  type Query, type SaveOrderInput,
  ShoppingCartItemTypeEnum, type UpdatePaymentInformationInput,
} from '@/generated/graphql';
import { useRootStore } from '@/stores/root';
import * as Arrays from '@/util/arrays';
import { Checkout } from '@vp/common';
import * as VPErrorLegacy from '@/types/VPError';
import { VPErrors } from '@vp/common';
import { groupBy } from '@/util/arrays';
import type { UseRecaptchaComposable } from '@/components/recaptcha/useRecaptcha';
import { DateTime } from 'luxon';
import { ApolloError, gql } from '@apollo/client/core';
import { NotFoundError } from '@/errors/NotFoundError';
import { useCookies } from '@vueuse/integrations/useCookies';
import * as Sentry from '@sentry/vue';
import { LoadingState } from '@/util/LoadingState';
import { useLanguageStore } from '@/checkout/languageStore';
import { SHOPPING_CART_TOKEN_COOKIE_KEY } from '@/checkout/constants';

const {
  OrderSummary: CheckoutOrderSummary,
  TicketBlueprint: CheckoutTicketBlueprint,
  ShoppingCartItemInput,
} = Checkout;

const SHOPPING_CART_FRAGMENT = Checkout.Api.SHOPPING_CART_FRAGMENT;

export class TicketLockingError extends Error {}
export class CaptchaVerificationError extends Error {}
export class InvalidRecaptchaTokenError extends Error {}

export interface TicketBlueprint {
  id: number;
  name: string;
  description?: string;
  price: number;
}

interface CheckoutState {
  _selectedEventId?: number | string;
  _recaptcha?: any;
  selectedEvent?: Checkout.CheckoutEvent.CheckoutEvent;
  /** @deprecated Use `selectedEventLoadingState` */
  selectedEventLoading: boolean;
  selectedEventLoadingState: LoadingState;
  ticketsLoading: LoadingState;
  tickets: Checkout.TicketBlueprint.CheckoutTicketBlueprint[];
  shoppingCartToken?: string;
  shoppingCart: Checkout.ShoppingCart.ShoppingCart;
  _shoppingCartLoading: boolean;
  shoppingCartLoadingState: LoadingState;
  shoppingCartLockLoading: LoadingState;
  orderSummary?: Checkout.OrderSummary.OrderSummary;
  orderSummaryLoading?: LoadingState;
}

const GET_AVAILABLE_TICKETS_QUERY = gql`
  query GetAvailableTickets($shoppingCartToken: String) {
    checkout {
      availableTickets(shoppingCartToken: $shoppingCartToken) {
        id
        eventId
        name
        ticketType
        description
        soldOut
        category
        categoryId
        minimum
        maximum
        multiple
        breakdown {
          price
          displayPrice
          convenienceFee
          taxFee
          ccFee
        }
      }
    }
  }
`;

const GET_ORDER_SUMMARY = gql`
  query GetOrderSummary($fingerprint: String!) {
    checkout {
      orderSummary(fingerprint: $fingerprint) {
        ...OrderSummaryFields
      }
    }
  }
  ${Checkout.Api.ORDER_SUMMARY_FIELDS}
`;

const getStoredCartToken = (): string | undefined => {
  if (!import.meta.env.SSR) {
    const cookies = useCookies();
    return cookies.get(SHOPPING_CART_TOKEN_COOKIE_KEY);
  }

  return undefined;
};

const storeCartToken = (token: string | null) => {
  if (import.meta.env.SSR) {
    return;
  }

  const cookies = useCookies();
  if (token) {
    // `path` will break things once we start supporting multiple events
    cookies.set(SHOPPING_CART_TOKEN_COOKIE_KEY, token, { path: '/e' });
  } else {
    cookies.remove(SHOPPING_CART_TOKEN_COOKIE_KEY);
  }
};

type OrderSummaryReponse = {
  orderSummary?: Checkout.OrderSummary.OrderSummary;
  errors?: VPErrors.VPError[];
};

export const useCheckoutStore = defineStore('checkout', {
  state: (): CheckoutState => ({
    selectedEventLoading: false,
    selectedEventLoadingState: LoadingState.UNKNOWN,
    selectedEvent: undefined,
    ticketsLoading: LoadingState.UNKNOWN,
    tickets: [],
    shoppingCart: Checkout.ShoppingCart.getNew(),
    shoppingCartToken: getStoredCartToken(),
    _shoppingCartLoading: false,
    shoppingCartLoadingState: LoadingState.UNKNOWN,
    shoppingCartLockLoading: LoadingState.UNKNOWN,
    orderSummaryLoading: LoadingState.UNKNOWN,
  }),
  getters: {
    initialized(state: CheckoutState): boolean {
      return !state.selectedEventLoading && !!state.selectedEvent;
    },

    getSelectedEventId(state: CheckoutState): number | string | undefined {
      if (state._selectedEventId) {
        return state._selectedEventId;
      }

      if (!import.meta.env.SSR) {
        const rootStore = useRootStore();
        return <string>rootStore.route?.params?.eventId;
      }
    },

    getSelectedEvent(state: CheckoutState): Checkout.CheckoutEvent.CheckoutEvent | undefined {
      return state.orderSummary?.event || state.selectedEvent;
    },

    selectedEventTickets(state: CheckoutState): Checkout.TicketBlueprint.CheckoutTicketBlueprint[] {
      return state.tickets.filter(({ eventId }) => this.getSelectedEvent?.id == eventId);
    },

    availableTickets(): Checkout.TicketBlueprint.CheckoutTicketBlueprint[] {
      return this.selectedEventTickets.filter(({ soldOut }) => !soldOut);
    },

    getCartedTickets(state: CheckoutState): Checkout.ShoppingCartItem.ShoppingCartItem[] {
      if (!state.shoppingCart) {
        return [];
      }

      const tickets = state.shoppingCart.items.filter(
        ({ itemType }) =>
          itemType === ShoppingCartItemTypeEnum.Ticket || itemType === ShoppingCartItemTypeEnum.SeatTicket,
      );

      const grouped = groupBy(tickets, 'typeHash');
      return Object.keys(grouped).reduce((acc, key): Checkout.ShoppingCartItem.ShoppingCartItem[] => {
        const ticketsInGroup = grouped[key];
        const summary = ticketsInGroup.reduce(
          (ticketAcc, ticket): Checkout.ShoppingCartItem.ShoppingCartItem => {
            const breakdown = ticketAcc.breakdown;

            return {
              ...ticket,
              count: ticketAcc.count + ticket.count,
              breakdown: {
                // This represents the price for 1 piece
                basePrice: ticket.breakdown?.basePrice,
                price: breakdown?.price + ticket.breakdown?.price,
                total: breakdown?.total + ticket.breakdown?.total,
              },
            };
          },
          { count: 0, breakdown: { price: 0, basePrice: 0, total: 0 } } as Checkout.ShoppingCartItem.ShoppingCartItem,
        );

        return [...acc, summary];
      }, [] as Checkout.ShoppingCartItem.ShoppingCartItem[]);
    },

    getCartedSeatTickets(state: CheckoutState): Checkout.ShoppingCartItem.ShoppingCartItem[] {
      if (!state.shoppingCart) {
        return [];
      }

      return state.shoppingCart.items.filter(({ itemType }) => itemType === ShoppingCartItemTypeEnum.SeatTicket);
    },

    getEventCheckoutConfig(state: CheckoutState): Checkout.CheckoutConfig.CheckoutConfig | undefined {
      return state.selectedEvent?.config;
    },

    getProcessorPublicKey(): string | undefined {
      return this.getEventCheckoutConfig?.processorPublicKey;
    },

    reCaptchaSiteKey(): string | undefined {
      return this.getEventCheckoutConfig?.recaptchaInvisibleSiteKey;
    },

    recaptchaCheckboxSiteKey(): string | undefined {
      return this.getEventCheckoutConfig?.recaptchaCheckboxSiteKey;
    },

    recaptchaEnabled(): boolean {
      return this.getEventCheckoutConfig?.enableCaptcha;
    },

    needsRecaptchaValidation(state: CheckoutState): boolean {
      if (!this.recaptchaEnabled) {
        return false;
      }

      if (!state.shoppingCart.notABotUntil) {
        return true;
      }

      const notABotUntil = DateTime.fromISO(state.shoppingCart.notABotUntil);

      return notABotUntil < DateTime.now();
    },

    hasValidLock(state: CheckoutState): boolean {
      if (!state.shoppingCart?.lockExpiresAt) {
        return false;
      }
      return DateTime.fromISO(state.shoppingCart.lockExpiresAt) > DateTime.now();
    },

    recaptcha(state: CheckoutState): UseRecaptchaComposable {
      return state._recaptcha as UseRecaptchaComposable;
    },
  },
  actions: {
    async init() {
      await this.fetchSelectedEvent();
      await this.loadShoppingCart();

      await this.fetchAvailableTickets();

      try {
        const languageStore = useLanguageStore();
        await languageStore.setLanguage(this.selectedEvent?.venue?.locale);
      } catch (e) {
        Sentry.captureException(e);
        console.error(e);
      }
    },

    async reset() {
      try {
        await this.releaseLock();
      } finally {
        // If release failed, we still want to clear the local data, we just can't do it before trying to release
      }

      this.setShoppingCartToken(null);
      this.shoppingCart = null;
      this.shoppingCartLoadingState = LoadingState.UNKNOWN;
      this.selectedEvent = undefined;
      this.selectedEventLoadingState = LoadingState.UNKNOWN;
      this.tickets = [];
      this.ticketsLoading = LoadingState.UNKNOWN;
      this.orderSummary = undefined;
      await this.init();
    },

    setShoppingCartToken(token: string | null) {
      storeCartToken(token);
      this.shoppingCartToken = token;
    },

    async fetchSelectedEvent() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      const selectedEventId = this.getSelectedEventId;

      if (this.getSelectedEvent) {
        return;
      }

      if (!selectedEventId) {
        throw 'No selected event id.';
      }

      if (this.selectedEventLoading) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      this.selectedEventLoadingState = LoadingState.LOADING;
      this.selectedEventLoading = true;
      try {
        this.selectedEvent = await Checkout.Api.getEvent(apiClient, selectedEventId);
        this.selectedEventLoadingState = LoadingState.DONE;
      } catch (e) {
        this.selectedEventLoadingState = LoadingState.ERROR;

        // instanceof doesn't work correctly in vike SSR
        if (e.constructor.name === 'ApolloError') {
          const error: ApolloError = <ApolloError>e;
          if (error.cause?.extensions?.code == 'NOT_FOUND') {
            this.selectedEventLoadingState = LoadingState.NOT_FOUND;
            throw new NotFoundError();
          }
        }
        throw e;
      } finally {
        this.selectedEventLoading = false;
      }
    },

    async fetchAvailableTickets({ force = false } = {}) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.ticketsLoading === LoadingState.LOADING) {
        return;
      }

      if (this.ticketsLoading == LoadingState.DONE && !force) {
        return;
      }

      this.ticketsLoading = LoadingState.LOADING;
      try {
        const { data } = await apiClient.query<Query>({
          query: GET_AVAILABLE_TICKETS_QUERY,
          variables: {
            shoppingCartToken: this.shoppingCartToken,
          },
          fetchPolicy: 'network-only',
        });

        if (!data.checkout.availableTickets) {
          this.ticketsLoading = LoadingState.ERROR;
          return;
        }

        this.tickets = data.checkout.availableTickets.map(CheckoutTicketBlueprint.fromGql);
        this.ticketsLoading = LoadingState.DONE;
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
      } catch (e) {
        this.ticketsLoading = LoadingState.ERROR;
      }
    },

    async loadShoppingCart() {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this._shoppingCartLoading || this.shoppingCartLoadingState === LoadingState.LOADING) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      if (this.shoppingCartLoadingState === LoadingState.DONE) {
        return;
      }

      this._shoppingCartLoading = true;
      this.shoppingCartLoadingState = LoadingState.LOADING;
      try {
        this.shoppingCart = await Checkout.Api.getShoppingCart(apiClient, this.shoppingCartToken, this.getSelectedEvent?.id);
        this.shoppingCartToken = this.shoppingCart.token;
        this.setShoppingCartToken(this.shoppingCartToken);
        this.shoppingCartLoadingState = LoadingState.DONE;
      } catch (e) {
        this.shoppingCartLoadingState = LoadingState.ERROR;
        throw e;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async setTicketCount(ticketId: number, count: number) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const [_, item] = Arrays.replaceOrAdd(
        this.shoppingCart.items.map(ShoppingCartItemInput.fromShoppingCartItem),
        ({ itemId }) => itemId == ticketId,
        () => ({ ...ShoppingCartItemInput.getNew(ticketId), count: Math.max(count, 0) }),
      );

      await this.storeShoppingCartItem(item);
    },

    async createPaymentInformation(payload: CreatePaymentInformationInput): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: CreatePaymentInformationInput!) {
              checkout {
                createPaymentInformation(input: $input) {
                  paymentInformation {
                    eventId
                    paymentIntent {
                      clientSecret
                      id
                    }
                    paymentProcessor
                  }
                  errors {
                    path
                    message
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.checkout?.createPaymentInformation?.errors) {
          return { errors: VPErrors.fromGqlCollection(data.checkout.createPaymentInformation.errors) };
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async updatePaymentInformation(payload: UpdatePaymentInformationInput): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        this.shoppingCartLoadingState = LoadingState.LOADING;
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: UpdatePaymentInformationInput!) {
              checkout {
                updatePaymentInformation(input: $input) {
                  paymentInformation {
                    eventId
                    paymentIntent {
                      clientSecret
                      id
                    }
                    paymentProcessor
                  }
                  errors {
                    path
                    message
                  }
                }
              }
            }
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.checkout?.createPaymentInformation?.errors) {
          return { errors: VPErrors.fromGqlCollection(data.checkout.createPaymentInformation.errors) };
        }

        return data;
      } finally {
        this.shoppingCartLoadingState = LoadingState.DONE;
      }
    },

    async validateNotABot(recaptcha?: UseRecaptchaComposable): Promise<boolean> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const runValidation = async (checkbox = false): Promise<false | string> => {
        const recaptchaResponse = checkbox
          ? await recaptcha.executeCheckbox('validate_cart')
          : await recaptcha.executeRecaptcha('validate_cart');

        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: ValidateNotABotInput!) {
              checkout {
                validateNotABot(input: $input) {
                  success
                  notABotUntil
                }
              }
            }
          `,
          variables: {
            input: {
              shoppingCartToken: this.shoppingCartToken,
              recaptchaResponse,
              checkbox,
            },
          },
        });

        const notABot = data?.checkout?.validateNotABot;

        if (!notABot?.success) {
          return false;
        }

        return notABot.notABotUntil;
      };

      try {
        let validUntil = await runValidation();

        if (!validUntil) {
          validUntil = await runValidation(true);
        }

        if (!validUntil) {
          return false;
        }

        this.shoppingCart.notABotUntil = validUntil;

        return true;
      } catch (e: any) {
        if (e instanceof ApolloError || e.constructor.name === 'ApolloError') {
          const error: ApolloError = <ApolloError>e;
          if (error.cause?.extensions?.code == 'INVALID_TOKEN') {
            throw new InvalidRecaptchaTokenError();
          }
        }
        throw e;
      }
    },

    async acquireLock(recaptcha?: UseRecaptchaComposable): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.shoppingCartLockLoading == LoadingState.LOADING) {
        return;
      }

      this.shoppingCartLockLoading = LoadingState.LOADING;

      try {
        if (this.needsRecaptchaValidation) {
          if (!(await this.validateNotABot(recaptcha))) {
            throw new Error("Please, prove you're not a bot");
          }
        }

        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: AcquireLockInput!) {
              checkout {
                acquireLock(input: $input) {
                  success
                  errors
                }
              }
            }
          `,
          variables: {
            input: {
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (!data?.checkout?.acquireLock?.success) {
          throw new TicketLockingError();
        }

        this.shoppingCartLockLoading = LoadingState.DONE;

        return data;
      } catch (e) {
        this.shoppingCartLockLoading = LoadingState.ERROR;
        throw e;
      }
    },

    async releaseLock(): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation ($input: ReleaseLockInput!) {
              checkout {
                releaseLock(input: $input) {
                  shoppingCart {
                    ...ShoppingCartFields
                  }
                  errors
                }
              }
            }
            ${SHOPPING_CART_FRAGMENT}
          `,
          variables: {
            input: {
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.checkout?.releaseLock?.shoppingCart) {
          this.shoppingCart = Checkout.ShoppingCart.fromGql(data.checkout.releaseLock.shoppingCart);
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async storeShoppingCartItem(item: Checkout.ShoppingCartItemInput.ShoppingCartItemInput) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      try {
        this.shoppingCartLoadingState = LoadingState.LOADING;

        if (this.needsRecaptchaValidation) {
          if (!this.recaptcha) {
            Sentry.captureException(new Error('recaptcha is not defined'));
          } else {
            if (!(await this.validateNotABot(this.recaptcha))) {
              throw new Error('Bot detected');
            }
          }
        }

        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation updateShoppingCart($input: UpdateShoppingCartInput!) {
              checkout {
                updateShoppingCart(input: $input) {
                  clientMutationId
                  errors
                  status
                  shoppingCart {
                    ...ShoppingCartFields
                  }
                }
              }
            }
            ${SHOPPING_CART_FRAGMENT}
          `,
          variables: {
            input: {
              changes: {
                eventId: this.getSelectedEvent!.id,
                items: [item],
              },
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        if (data?.checkout?.updateShoppingCart?.shoppingCart) {
          this.shoppingCart = Checkout.ShoppingCart.fromGql(data.checkout.updateShoppingCart.shoppingCart);
        }

        const errors = data?.checkout?.updateShoppingCart?.errors;
        if (errors?.length) {
          throw VPErrorLegacy.getNew(errors);
        }

        if (data?.checkout?.updateShoppingCart?.status === 'PASSWORD_REQUIRED') {
          throw new Checkout.ShoppingCartError('PASSWORD_REQUIRED');
        }
      } finally {
        this.shoppingCartLoadingState = LoadingState.DONE;
      }
    },

    async saveOrder(payload: SaveOrderInput): Promise<OrderSummaryReponse> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation SaveOrder ($input: SaveOrderInput!) {
              checkout {
                saveOrder(input: $input) {
                  clientMutationId
                  errors {
                    path
                    message
                  }
                  orderSummary {
                    ...OrderSummaryFields
                  }
                  changeToken {
                    token
                    expiresAt
                  }
                  success
                }
              }
            }
            ${Checkout.Api.ORDER_SUMMARY_FIELDS}
          `,
          variables: {
            input: {
              ...payload,
              shoppingCartToken: this.shoppingCartToken,
            },
          },
        });

        const result: OrderSummaryReponse = {};

        if (data?.checkout?.saveOrder?.orderSummary) {
          this.orderSummary = CheckoutOrderSummary.fromGql(data?.checkout?.saveOrder?.orderSummary);
          result.orderSummary = this.orderSummary;
        }
        if (data?.checkout?.saveOrder?.errors) {
          result.errors = VPErrors.fromGqlCollection(data.checkout.saveOrder.errors);
        }

        return result;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async completeOrder(payload: CompleteOrderInput): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;
      this._shoppingCartLoading = true;

      try {
        const { data } = await apiClient.mutate<Mutation>({
          mutation: gql`
            mutation CompleteOrder ($input: CompleteOrderInput!) {
              checkout {
                completeOrder(input: $input) {
                  clientMutationId
                  errors {
                    path
                    message
                  }
                  orderSummary {
                    ...OrderSummaryFields
                  }
                  changeToken {
                    token
                    expiresAt
                  }
                  success
                }
              }
            }
            ${Checkout.Api.ORDER_SUMMARY_FIELDS}
          `,
          variables: {
            input: {
              ...payload,
            },
          },
        });

        if (data?.checkout?.completeOrder?.orderSummary) {
          this.orderSummary = CheckoutOrderSummary.fromGql(data?.checkout?.completeOrder?.orderSummary);
        }

        return data;
      } finally {
        this._shoppingCartLoading = false;
      }
    },

    async updateOrderEmail(fingerprint: string, changeToken: string, email: string): Promise<any> {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      const { data } = await apiClient.mutate({
        mutation: gql`
          mutation ($input: UpdateOrderEmailMutationInput!) {
            checkout {
              updateOrderEmail(input: $input) {
                success
                orderSummary {
                  ...OrderSummaryFields
                }
              }
            }
          }
          ${Checkout.Api.ORDER_SUMMARY_FIELDS}
        `,
        variables: {
          input: {
            fingerprint,
            changeToken,
            email,
          },
        },
      });

      if (data?.checkout?.updateOrderEmail?.success) {
        this.orderSummary = CheckoutOrderSummary.fromGql(data?.checkout?.updateOrderEmail?.orderSummary);
      }
    },

    async loadOrderSummary(fingerprint: string) {
      const connectionStore = useConnectionStore();
      const apiClient = connectionStore.apiClient!;

      if (this.orderSummaryLoading == LoadingState.LOADING) {
        // TODO: wait until finished loading and check if the required event is loaded
        return;
      }

      try {
        this.orderSummaryLoading = LoadingState.LOADING;
        const { data } = await apiClient.query<Query>({
          query: GET_ORDER_SUMMARY,
          fetchPolicy: 'network-only',
          variables: {
            fingerprint,
          },
        });
        this.orderSummary = CheckoutOrderSummary.fromGql(data.checkout.orderSummary);
        this.orderSummaryLoading = LoadingState.DONE;
      } catch (e) {
        this.orderSummaryLoading = LoadingState.ERROR;
        throw e;
      }
    },
  },
});
